package info.rezident.app.domain

import info.rezident.app.core.Logger
import info.rezident.app.data.AiRepository
import info.rezident.app.data.quiz.QuizTemplateRepository
import info.rezident.app.data.quiz.RecommendationItem

/**
 * UseCase for generating AI-powered explanatory text for pros/cons
 * from question IDs returned by the Cloud Function.
 *
 * Responsibilities:
 * - Detect if pros/cons are question IDs (numeric strings)
 * - Randomly select max 5 items from each list
 * - Fetch question/factor text for each ID
 * - Generate AI explanations in a single batch request for ALL specialties (to avoid quota exhaustion)
 * - Return updated RecommendationItem with generated text
 */
object GenerateProConsExplanationsUseCase {
    private const val MAX_ITEMS_PER_LIST = 5

    /**
     * Generate explanatory text for MULTIPLE recommendation items in a SINGLE AI request.
     * This is the preferred method to avoid quota exhaustion.
     *
     * @param items List of recommendation items with pros/cons as question IDs
     * @return List of updated recommendation items with AI-generated explanatory text
     */
    suspend fun executeBatch(items: List<RecommendationItem>): List<RecommendationItem> {
        // Collect all questions from all specialties based on the 4 analysis categories
        val questionsBySpecialty = mutableMapOf<String, MutableList<AiRepository.QuestionToExplain>>()

        // Track which items need generation (have IDs in any of the 4 categories)
        val itemsNeedingGeneration = items.filter { item ->
            item.perfectAnswersHigh.any { isQuestionId(it) } ||
                    item.perfectAnswersLow.any { isQuestionId(it) } ||
                    item.differentAnswersHigh.any { isQuestionId(it) } ||
                    item.differentAnswersLow.any { isQuestionId(it) }
        }

        if (itemsNeedingGeneration.isEmpty()) {
            Logger.log("No items need pros/cons generation")
            return items
        }

        Logger.log("Collecting max 5 perfect + max 5 different answers for ${itemsNeedingGeneration.size} specialties")

        // Collect questions for each specialty: max 5 from perfect (High+Low) and max 5 from different (High+Low)
        for (item in itemsNeedingGeneration) {
            val questionsForSpecialty = mutableListOf<AiRepository.QuestionToExplain>()

            // Combine perfectAnswersHigh + perfectAnswersLow, shuffle, and take max 5 total
            val allPerfectAnswers = (item.perfectAnswersHigh + item.perfectAnswersLow)
                .filter { isQuestionId(it) }
                .shuffled()
                .take(MAX_ITEMS_PER_LIST)

            for (questionId in allPerfectAnswers) {
                val questionText = QuizTemplateRepository.getQuestionOrFactorText(questionId)
                if (questionText != null) {
                    // Determine match type based on which category it came from
                    val matchType = if (item.perfectAnswersHigh.contains(questionId)) {
                        AiRepository.MatchType.PERFECT_HIGH
                    } else {
                        AiRepository.MatchType.PERFECT_LOW
                    }

                    questionsForSpecialty.add(
                        AiRepository.QuestionToExplain(
                            specialty = item.name,
                            questionId = questionId,
                            questionText = questionText,
                            isPro = true,
                            matchType = matchType
                        )
                    )
                } else {
                    Logger.warn("Question text not found for ID: $questionId")
                }
            }

            // Combine differentAnswersHigh + differentAnswersLow, shuffle, and take max 5 total
            val allDifferentAnswers = (item.differentAnswersHigh + item.differentAnswersLow)
                .filter { isQuestionId(it) }
                .shuffled()
                .take(MAX_ITEMS_PER_LIST)

            for (questionId in allDifferentAnswers) {
                val questionText = QuizTemplateRepository.getQuestionOrFactorText(questionId)
                if (questionText != null) {
                    // Determine match type based on which category it came from
                    val matchType = if (item.differentAnswersHigh.contains(questionId)) {
                        AiRepository.MatchType.DIFFERENT_HIGH
                    } else {
                        AiRepository.MatchType.DIFFERENT_LOW
                    }

                    questionsForSpecialty.add(
                        AiRepository.QuestionToExplain(
                            specialty = item.name,
                            questionId = questionId,
                            questionText = questionText,
                            isPro = false,
                            matchType = matchType
                        )
                    )
                } else {
                    Logger.warn("Question text not found for ID: $questionId")
                }
            }

            if (questionsForSpecialty.isNotEmpty()) {
                questionsBySpecialty[item.name] = questionsForSpecialty
                Logger.log("${item.name}: ${questionsForSpecialty.size} questions collected (${questionsForSpecialty.count { it.isPro }} pros, ${questionsForSpecialty.count { !it.isPro }} cons)")
            }
        }

        // Generate all explanations in a single batch request
        val explanationsMap = if (questionsBySpecialty.isNotEmpty()) {
            Logger.log("Generating explanations for ${questionsBySpecialty.size} specialties in a single batch request")
            AiRepository.generateBatchProConsExplanationsForAllSpecialties(questionsBySpecialty)
        } else {
            emptyMap()
        }

        Logger.log("Generated ${explanationsMap.size} explanations total")

        // Update each item with its generated explanations
        return items.map { item ->
            val needsGeneration = itemsNeedingGeneration.any { it.name == item.name }
            if (!needsGeneration) {
                return@map item
            }

            // Separate pros and cons from explanations
            val generatedPros = mutableListOf<String>()
            val generatedCons = mutableListOf<String>()

            // Combine perfect answers (High + Low) and extract max 5 total pros
            val allPerfectAnswers = (item.perfectAnswersHigh + item.perfectAnswersLow)
                .filter { isQuestionId(it) }
                .shuffled()
                .take(MAX_ITEMS_PER_LIST)

            allPerfectAnswers.forEach { questionId ->
                explanationsMap["${item.name}:$questionId"]?.let { generatedPros.add(it) }
            }

            // Combine different answers (High + Low) and extract max 5 total cons
            val allDifferentAnswers = (item.differentAnswersHigh + item.differentAnswersLow)
                .filter { isQuestionId(it) }
                .shuffled()
                .take(MAX_ITEMS_PER_LIST)

            allDifferentAnswers.forEach { questionId ->
                explanationsMap["${item.name}:$questionId"]?.let { generatedCons.add(it) }
            }

            Logger.log("${item.name}: Generated ${generatedPros.size} pros and ${generatedCons.size} cons")

            item.copy(
                pros = generatedPros,
                cons = generatedCons
            )
        }
    }

    /**
     * Check if a string is a question ID (numeric string)
     */
    private fun isQuestionId(text: String): Boolean {
        return text.toIntOrNull() != null
    }
}